import styled from 'styled-components'
import React, { useCallback, useMemo } from 'react'

import { StyledSelect } from 'Components/Inputs/StyledSelect'
import { ClearFiltersButton } from 'Components/Table/ClearFiltersButton'
import { InvoiceTableFilters } from 'Pages/Employer/BillingPage/InvoiceTable/InvoiceTable'
import { InvoiceStateLabels } from 'Pages/Employer/BillingPage/InvoiceTable/InvoiceStatus'
import { useCountries } from 'Hooks/useCountries'

import { InvoiceState } from 'Utils/types/invoice'
import { FlexColumn, GridColumn, spacingMD, spacingSM } from 'Utils/styles/spacing'
import { TableLabel } from 'Utils/styles/text'
import { CountryCode } from 'Utils/types/countries'

const ActionsContainer = styled.div<{ countryFilter: boolean }>`
  display: grid;
  grid-column-gap: ${spacingMD};
  align-items: center;
  grid-template-columns: ${(countryFilter) => (countryFilter ? '50% 50%' : '100%')};
  padding-bottom: ${spacingMD};
`

type PeopleTableFiltersProps = {
  filters: InvoiceTableFilters
  updateFilters: (newFilters: Partial<InvoiceTableFilters>) => void
  clearFilters?: () => void
  disabled: boolean
}

export const InvoiceTableActions = ({
  filters,
  updateFilters,
  clearFilters,
  disabled
}: PeopleTableFiltersProps) => {
  const handleStateChange = useCallback(
    (state: InvoiceState | '') => updateFilters({ state }),
    [updateFilters]
  )

  const handleCountryChange = useCallback(
    (countryCode: CountryCode | '') => updateFilters({ countryCode }),
    [updateFilters]
  )

  const { hasMultipleCountries, countryOptionsWithAll } = useCountries()

  const stateOptions = useMemo<{ value: InvoiceState | ''; label: string }[]>(
    () => [
      { value: '', label: 'All statuses' },
      ...Object.values(InvoiceState).map((state) => ({
        value: state,
        label: InvoiceStateLabels[state]
      }))
    ],
    []
  )

  return (
    <FlexColumn gap={spacingSM}>
      <GridColumn>
        <TableLabel>Invoices</TableLabel>
        <ClearFiltersButton onClear={clearFilters} />
      </GridColumn>
      <ActionsContainer countryFilter={hasMultipleCountries}>
        <StyledSelect
          handleChange={handleStateChange}
          options={stateOptions}
          defaultValue={filters.state}
          disabled={disabled}
        />
        {hasMultipleCountries && (
          <StyledSelect
            handleChange={handleCountryChange}
            options={countryOptionsWithAll}
            disabled={disabled}
            defaultValue={filters.countryCode}
          />
        )}
      </ActionsContainer>
    </FlexColumn>
  )
}
